import { ecId, ecLean, HandBack, Refusal, record, upper, type JanusCtx, type Row } from '../janus-engine/ability/context.js';
import { normalizePhpListish } from '../shared/php-listish.js';
import { paoeEntryContext } from '../janus-engine/ability/paoe-entry.js';
import { browserRollSource } from '../janus-engine/actions/roll-source.js';
import { payloadDryRunRollSource, PAYLOAD_DRY_PATTERNS } from '../janus-engine/ability/environmental-payload.js';
import type { ActivityPlan } from '../janus-engine/actions/activity.js';
import { abilityTruthCampaign, abilityTruthReaders } from './ability-truth.js';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { activityAppliedFrame } from '../janus-engine/transport/activity-applied-frame';
import { actorEncounterFor, actorRowFor, bankConfirmedRev, confirmedRevFor, waitForActorMovement } from './movement-truth.js';
import { fetchAndCacheCaster } from './caster-cache.js';
export { ensureActivityMembers } from './activity-members.js';
import { fetchAndCacheObject } from './object-cache.js';
import { readInventoryField } from '../janus-engine/compile/inventory-raw-readers.js';
import { readItemField } from '../janus-engine/compile/item-field-reader.js';
import { planPickupLight, BURNABLE_LIGHT_IDS } from '../janus-engine/inventory/pickup-light-20.js';
import { findInventoryRow } from '../janus-engine/inventory/flag-adjust.js';
import { statusItemId, statusRows } from '../janus-engine/statuses/expiry.js';
import { spawnRowIdentity } from '../janus-engine/spawn/row-plan.js';
import type { InventoryMoveReceipt } from '../ft3d-move-handlers/inventory-move-receipt.js';
import { ftCurrentTruthSink } from '../shared/truth-sink';

const ENDPOINT = '/options/game/actions/activity_truth_commit_inc.php';
/** Retain undelivered commands: a lost response never turns a second click into a second draw/commit. */
const undelivered = new Map<string, Row>();
const refreshRequired = new Set<string>();
const refreshes = new Map<string, Promise<boolean>>();
type ActivityContext = JanusCtx & { activityExpectedRev: number };
export function activityContext(encounterId: string, actorId: string, actor: Row, completeReactions: ReadonlySet<string> = new Set()): ActivityContext {
  const encounter = record(actorEncounterFor(encounterId));
  if (!encounter) throw new HandBack('activity:encounter-not-held');
  const members = new Map<string, Row>(normalizePhpListish(encounter.encounter_creatures_arr).flatMap(raw => {
    const row = record(raw); return row && ecId(row) ? [[ecId(row), row] as [string, Row]] : [];
  }));
  members.set(upper(actorId), actor);
  const member = actorRowFor(encounterId, actorId);
  if (!member) throw new HandBack('activity:actor-not-held');
  return { ...paoeEntryContext(encounter, members, abilityTruthReaders(actor, encounter, completeReactions), browserRollSource()),
    activityExpectedRev: confirmedRevFor(encounterId, actorId, member) };
}

/** Every branch is visited without live dice first. Refusals never spend a draw. */
export function decideActivity<T extends ActivityPlan>(ctx: ActivityContext, decide: (ctx: JanusCtx) => T): T {
  const fresh = (roll: JanusCtx['roll'], dryRun: boolean) => ({ ...paoeEntryContext(ctx.encounter, ctx.originals, ctx.read, roll), dryRun });
  for (const pattern of PAYLOAD_DRY_PATTERNS) decide(fresh(payloadDryRunRollSource(pattern), true));
  return { ...decide(fresh(ctx.roll, false)), expectedRev: ctx.activityExpectedRev };
}

export function activityError(error: unknown): { ok: false; error: string } {
  const reason = error instanceof HandBack ? error.reason : error instanceof Refusal ? error.refusal : String(error);
  displayWarning(reason); return { ok: false, error: reason };
}

export async function loadActivityStatus(encounterId: string, actorId: string, itemId: string): Promise<Row | null> {
  const response = await fetch('/options/api/activity_state_inc.php', { method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ schema: 'janus.activity.truth.v1', operation_id: crypto.randomUUID().toUpperCase(), actor_id: actorId,
      encounter_id: encounterId, expected_rev: 0, kind: 'status', item_id: itemId }), signal: AbortSignal.timeout(15000) });
  const body = record(await response.json());
  if (!response.ok || body?.ok !== true || !Object.prototype.hasOwnProperty.call(body, 'actor_status') || body.actor_status !== null && !record(body.actor_status))
    throw new HandBack('activity:status-snapshot-not-held');
  return record(body.actor_status);
}

// Equipment is imported by several page aggregates; keep the stateful websocket reader/transport in this bundle.
Object.assign(window, {
  ftLoadActivityStatus: loadActivityStatus,
  ftInventoryEquipActivityReaders: (actor: Row, encounter: Row) => abilityTruthReaders(actor, encounter, new Set([ecId(actor)])),
});

/** Recover an unanswered operation before the caller hydrates or draws for its next action. */
export async function recoverActivity(encounterId: string, actorId: string): Promise<void> {
  const key = upper(encounterId) + ':' + upper(actorId), command = undelivered.get(key);
  if (command) await deliverActivity(key, command);
  if (refreshRequired.has(key) && !await refreshActivity(encounterId, actorId)) throw new HandBack('activity:saved-state-refresh-pending');
}

export async function saveActivity(encounterId: string, actorId: string, kind: string, plan: ActivityPlan, keepalive = false, operationId?: string): Promise<Row> {
  await waitForActorMovement(encounterId, actorId);
  const key = upper(encounterId) + ':' + upper(actorId), earlier = undelivered.get(key);
  if (earlier) throw new HandBack('activity:previous-save-unconfirmed');
  const actor = actorRowFor(encounterId, actorId);
  if (!actor) throw new HandBack('activity:actor-not-held');
  if (!Number.isInteger(plan.expectedRev)) throw new HandBack('activity:revision-snapshot-missing');
  const command: Row = { schema: 'janus.activity.truth.v1', operation_id: operationId ?? crypto.randomUUID().toUpperCase(),
    encounter_id: upper(encounterId), actor_id: upper(actorId), expected_rev: plan.expectedRev, kind,
    writes: plan.writes, rows: plan.rows, connections: plan.connections ?? {}, roll_private: [0, 1, 2].includes(Number(plan.rollPrivate)) ? Number(plan.rollPrivate) : 0 };
  undelivered.set(key, command);
  if (plan.spawnRows) command.spawnRows = plan.spawnRows;
  if (window.ftPushAppliedTruth) {
    try {
      const encounter = record(actorEncounterFor(encounterId));
      if (!encounter) throw new HandBack('activity:encounter-not-held');
      const applied_frame = activityAppliedFrame({ operation_id: String(command.operation_id), encounter_id: upper(encounterId), actor_id: upper(actorId),
        rev: plan.expectedRev! + 1, member_revs: { [upper(actorId)]: plan.expectedRev! + 1 } }, plan, encounter,
        abilityTruthCampaign(encounter), abilityTruthReaders(actor, encounter, new Set()).definition);
      window.ftPushAppliedTruth({ type: 'janus_minigame_truth', command: { ...command, schema: 'janus.minigame.truth.v1',
        operation_id: String(command.operation_id), encounter_id: upper(encounterId), actor_id: upper(actorId), action: 'activity', rev: applied_frame.rev }, applied_frame });
    } catch (error) { undelivered.delete(key); throw error; }
  }
  return deliverActivity(key, command, keepalive);
}

async function deliverActivity(key: string, command: Row, keepalive = false): Promise<Row> {
  const encounterId = String(command.encounter_id), actorId = String(command.actor_id);
  for (let attempt = 0; attempt < 3; ++attempt) {
    try {
      const response = await saveAppliedTruth(ENDPOINT, { operation_id: String(command.operation_id) }, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command), signal: AbortSignal.timeout(20000), keepalive });
      const body = record(await response.json().catch(() => null));
      if (body?.ok === true) {
        undelivered.delete(key);
        const actor = actorRowFor(encounterId, actorId);
        if (actor) bankConfirmedRev(encounterId, actorId, actor, Number(body.rev ?? ecLean(actor, 'movement_rev')));
        const patch = record(body.base_patch);
        // The pushed frame installed this decision before the save. Its ledger
        // echo belongs to the common write lane; do not reread the actor here.
        if (window.ftPushAppliedTruth) {
          window.dispatchEvent(new CustomEvent('ft:activity-truth-committed', { detail: { command, result: body } }));
          return body;
        }
        if (patch?.type === 'base_patch' && Array.isArray(patch.ops) && !(body.replay && !patch.ops.length) && window.ftApplyBasePatch) {
          refreshRequired.add(key);
          try {
            await window.ftApplyBasePatch(patch as unknown as Parameters<NonNullable<Window['ftApplyBasePatch']>>[0], true);
            refreshRequired.delete(key);
          } catch { /* Saved; recover the missing installation before another decision. */ }
        } else await refreshActivity(encounterId, actorId);
        window.dispatchEvent(new CustomEvent('ft:activity-truth-committed', { detail: { command, result: body } }));
        return body;
      }
      if (response.status >= 400 && (response.status < 500 || body?.ok === false || response.status === 500 && body === null)) {
        undelivered.delete(key); throw new HandBack(String(body?.error ?? 'activity:save-refused'));
      }
    } catch (error) { if (error instanceof HandBack) throw error; }
  }
  throw new HandBack('activity:save-unconfirmed');
}

export function refreshActivity(encounterId: string, actorId: string): Promise<boolean> {
  const key = upper(encounterId) + ':' + upper(actorId), active = refreshes.get(key);
  if (active) return active;
  refreshRequired.add(key);
  const next = (async () => {
    try {
      await window.ftResyncEncounter?.(encounterId);
      await fetchAndCacheCaster(actorId, { skipEncounterSeed: true, throwOnError: true });
      refreshRequired.delete(key);
      return true;
    } catch { return false; }
  })().finally(() => { refreshes.delete(key); });
  refreshes.set(key, next); return next;
}

/** Receipt observers cover ordinary Pickup and Take All without sending rules to the raw transfer route. */
export async function settlePickupLight(receipt: InventoryMoveReceipt, actorId: string): Promise<void> {
  // Memory pickup refuses light timers before committing. Its synthetic ids have no saved objects to recover.
  if (ftCurrentTruthSink()) return;
  if (receipt.move.outcome !== 'full_move' || receipt.move.destination.deleted_after) return;
  const sourceId = upper(receipt.move.source.possess_id_before);
  if (sourceId === upper(receipt.move.destination.possess_id) || receipt.move.roots.source) return;
  const encounterId = upper(receipt.affected_encounter_ids.find(id => actorEncounterFor(id)
    && actorRowFor(id, actorId)) ?? '');
  if (!encounterId || !actorId) return;
  const id = upper(receipt.move.destination.inventory_id), key = 'ft.pickup-light.' + upper(receipt.mutation_id);
  const operationId = spawnRowIdentity(`pickup-light|${receipt.mutation_id}`);
  const pending = undelivered.get(encounterId + ':' + upper(actorId));
  await recoverActivity(encounterId, actorId);
  const confirmed = (): void => {
    window.sessionStorage.removeItem(key);
    window.dispatchEvent(new CustomEvent('ft:inventory-pickup-light-saved', { detail: { inventoryId: id, operationId: receipt.mutation_id } }));
  };
  // Recovery delivered this exact frozen decision; do not submit/draw it a second time.
  if (pending?.operation_id === operationId) { confirmed(); return; }
  const actor = record(actorRowFor(encounterId, actorId));
  if (!actor) throw new HandBack('inventory-pickup:actor-not-held');
  const encounter = record(actorEncounterFor(encounterId));
  const held = (inventoryId: string): Row | null => findInventoryRow([actor.inventory, encounter?.encounter_inventories_arr], inventoryId);
  const sourceType = receipt.affected_encounter_ids.some(id => upper(id) === sourceId) ? 'encounter'
    : String(readItemField(held(sourceId) ?? record(window.FateTide?.objectCache?.get(sourceId))
      ?? record(await fetchAndCacheObject(sourceId)), 'item_type') ?? '');
  if (!['encounter', 'location'].includes(sourceType)) return;
  const source = held(id) ?? record(await fetchAndCacheObject(id));
  if (!source || upper(readInventoryField(source, 'possess_id')) !== upper(receipt.move.destination.possess_id)) return;
  const lights = statusRows(source, 'target').filter(status => BURNABLE_LIGHT_IDS.has(statusItemId(status)));
  if (!lights.length) return;
  const ctx = activityContext(encounterId, actorId, actor);
  const missing = new Set(lights.filter(status => {
    const inline = record(status.status_arr);
    return !(inline && (record(inline.item_layer_types) || record(inline.layers))) && !ctx.read.definition(statusItemId(status));
  }).map(statusItemId));
  await Promise.all([...missing].map(item => fetchAndCacheObject(item)));
  const saved = window.sessionStorage.getItem(key);
  const plan: ActivityPlan = saved ? JSON.parse(saved) as ActivityPlan : planPickupLight({ source, actor, quantity: receipt.move.moved_qty,
    sourceType, moveType: 'move_inventory', merged: false }, { duration: ctx.read, definition: ctx.read.definition, die: sides => ctx.roll.d(sides) });
  if (!plan.writes.length) return;
  plan.expectedRev ??= ctx.activityExpectedRev;
  window.sessionStorage.setItem(key, JSON.stringify(plan));
  await saveActivity(encounterId, actorId, 'inventory_pickup_light', plan, false, operationId);
  confirmed();
}
let pickupQueue: Promise<void> = Promise.resolve();
const queuePickup = (receipt: InventoryMoveReceipt, actorId: string): void => {
  pickupQueue = pickupQueue.then(() => settlePickupLight(receipt, actorId)).catch(error => {
    displayWarning('The item was picked up; its light timer could not be confirmed.');
    window.dispatchEvent(new CustomEvent('ft:inventory-pickup-light-failed', { detail: { reason: String(error), inventoryId: receipt.move.destination.inventory_id } }));
  });
};
window.addEventListener('ft:inventory-move-receipt-applied', event => {
  const receipt = (event as CustomEvent<InventoryMoveReceipt>).detail;
  queuePickup(receipt, upper(receipt.move.roots.destination ?? window.ftGetActiveCasterId?.()));
});
window.addEventListener('ft:inventory-take-all-receipt-applied', event => {
  const batch = record((event as CustomEvent).detail);
  if (!batch || !Array.isArray(batch.moves)) return;
  const actorId = upper(window.ftGetActiveCasterId?.()), encounterId = upper(window.ftGetActiveEncounterId?.());
  for (const move of batch.moves) queuePickup({ schema: 'ft3d.inventory-move-receipt.v1', ok: true, mutation_id: crypto.randomUUID().toUpperCase(),
    move, affected_caster_ids: [actorId], affected_encounter_ids: [encounterId], timing: { accepted_unix_ms: Date.now(), commit_ms: 0 } }, actorId);
});
